/**
 * Query string utilities for URL parameters
 */

/**
 * Configuration for a single query parameter
 */
export interface QueryParamConfig {
  key: string;
  type: "string" | "number" | "boolean";
  skipValue?: string;
  onlyIfTrue?: boolean;
}

/**
 * Build a query string from parameters and configuration
 *
 * @param params - Object containing parameter values
 * @param config - Array of parameter configurations
 * @returns URL-encoded query string
 */
export function buildQueryString(
  params: Record<string, unknown>,
  config: QueryParamConfig[],
): string {
  const query = new URLSearchParams();

  config.forEach(({ key, type, skipValue, onlyIfTrue }) => {
    const value = params[key];

    // Skip empty values
    if (value === undefined || value === null || value === "") {
      return;
    }

    // Skip specific values (e.g., 'any')
    if (skipValue !== undefined && value === skipValue) {
      return;
    }

    if (type === "boolean") {
      // For boolean, optionally only include if true
      if (onlyIfTrue && value !== true) {
        return;
      }
      query.append(key, value ? "true" : "false");
    } else {
      query.append(key, String(value));
    }
  });

  return query.toString();
}

/**
 * Parse a query string into parameters based on configuration
 *
 * @param search - The query string (including leading ?)
 * @param config - Array of parameter configurations
 * @returns Object with parsed parameters
 */
export function parseQueryString(
  search: string,
  config: QueryParamConfig[],
): Record<string, unknown> {
  const query = new URLSearchParams(search);
  const params: Record<string, unknown> = {};

  config.forEach(({ key, type }) => {
    const value = query.get(key);

    if (value === null) {
      return;
    }

    if (type === "number") {
      const num = Number(value);
      if (!isNaN(num)) {
        params[key] = num;
      }
    } else if (type === "boolean") {
      params[key] = value === "true";
    } else {
      params[key] = value;
    }
  });

  return params;
}

/**
 * Helper to create multiple string parameter configurations
 */
export function createStringParamConfigs(
  keys: readonly string[],
  skipValue?: string,
): QueryParamConfig[] {
  return keys.map((key) => ({ key, type: "string", skipValue }));
}

/**
 * Helper to create multiple number parameter configurations
 */
export function createNumberParamConfigs(
  keys: readonly string[],
): QueryParamConfig[] {
  return keys.map((key) => ({ key, type: "number" }));
}

/**
 * Helper to create multiple boolean parameter configurations
 */
export function createBooleanParamConfigs(
  keys: readonly string[],
  onlyIfTrue = true,
): QueryParamConfig[] {
  return keys.map((key) => ({ key, type: "boolean", onlyIfTrue }));
}
